import ConfirmDialog from 'components/dialogs/ConfirmDialog';
import { useTranslation } from 'react-i18next';
import PromptDialog from 'components/dialogs/PromptDialog';
import useModal from 'hooks/useModal';
import { PartyModel, PartyType } from 'models/PartyModel';
import PartyDialog from 'modules/parties/dialogs/PartyDialog';
import ChangeTypeDialog from 'modules/parties/dialogs/ChangeTypeDialog';
import MergePartiesDialog from 'modules/parties/dialogs/MergePartiesDialog';
import useParties from 'modules/parties/hooks/useParties';
import Api from 'utils/api';


const usePartyModals = () => {
    const {t} = useTranslation();
    const {showModal, hideModal} = useModal();
    const {updateParty, createParty, attachParty, detachParty, setPartyType, mergeParty} = useParties();

    const openAddPartyModal = (parentType: string, parentId: number, type: PartyType, emails: string[]) => showModal(PartyDialog,
        {
            create: true,
            emails,
            party: {
                type,
                country: 'RU',
                phone: '+7'
            } as PartyModel
        },
        {
            title: t('add_title', {value: t(type)}),
            maxWidth: 'sm',
            fullWidth: true,
        })
        .then(form => {
            if (form.action === 'attach') {
                return attachParty(parentType, parentId, form.party.id)
            } else {
                return createParty(parentType, parentId, form.party);
            }
        })
        .then(hideModal);

    const openEditPartyModal = (party: PartyModel) => showModal(PartyDialog,
        {party},
        {
            title: t('edit_title', {value: t(party.type)}),
            maxWidth: 'sm',
            fullWidth: true,
        })
        .then(form => updateParty(form.party).then(hideModal).then(() => form.party));

    const openInviteManagerModal = (party: PartyModel) => {
        if (party.email) {
            return showModal(ConfirmDialog,
                {
                    description: party.name,
                },
                {
                    title: t('confirm_invite_manager'),
                })
                .then(() => Api.users.inviteParty(party.id))
                .then(res => {
                    hideModal();
                    return {
                        ...party,
                        manager: res.data.data.id,
                    }
                });
        }

        return showModal(PromptDialog,
            {
                required: true,
            },
            {
                title: t('specify_email_for', {value: party.name}),
                maxWidth: 'xs',
                fullWidth: true,
            })
            .then(email => Api.users.inviteParty(party.id, email))
            .then(res => {
                hideModal();
                return {
                    ...party,
                    email: res.data.data.email,
                    manager: res.data.data.id,
                }
            });
    }

    const openChangePartyTypeModal = (party: PartyModel) => showModal(ChangeTypeDialog,
        {party},
        {
            title: t('change_party_type'),
            maxWidth: 'xs',
            fullWidth: true,
        })
        .then(type => setPartyType(party.id, type))
        .then(hideModal);

    const openMergePartiesModal = (parties: PartyModel[]) => showModal(MergePartiesDialog,
        {parties},
        {
            title: t('merge_parties'),
            maxWidth: 'md',
            fullWidth: true,
        })
        .then(mergeParty)
        .then(hideModal);

    const confirmDetachParty = (parent: string, parentId: number, party: PartyModel) => showModal(ConfirmDialog,
        {
            description: party.name,
        },
        {
            title: t('confirm_detach_party'),
            fullScreen: false,
        })
        .then(() => detachParty(parent, parentId, party.id))
        .finally(hideModal)

    return {
        openAddPartyModal,
        openEditPartyModal,
        confirmDetachParty,
        openChangePartyTypeModal,
        openMergePartiesModal,
        openInviteManagerModal,
    };
};

export default usePartyModals;
